import { EnvironmentVariables } from 'services/environment';
import { datadogRum } from '@datadog/browser-rum';
import { FullStory, isInitialized } from '@fullstory/browser';
import type { RumFetchResourceEventDomainContext } from '@datadog/browser-rum';
import { getCurrentProfile } from '@updater/identity';
import { datadogConfig } from 'config';

type ApolloErrorContext = Error & {
  clientErrors: unknown[];
  extraInfo: unknown;
  graphQLErrors: unknown[];
  message: string;
  networkError: TypeError & { cause?: Record<string, string> };
};

const errorIsApolloNetworkErrorWithCause = (
  object: unknown
): object is ApolloErrorContext => {
  return (
    !!object &&
    typeof object === 'object' &&
    'networkError' in object &&
    typeof (object as ApolloErrorContext).networkError === 'object' &&
    (object as ApolloErrorContext).networkError !== null &&
    'cause' in (object as ApolloErrorContext).networkError
  );
};

// In SDK v5+, the string form of `proxy` sends a relative path to the worker
// instead of a full absolute URL. The worker does fetch(destinationUrl) which
// requires an absolute URL, so we use the function form to construct it.
const buildProxyUrl = ({
  path,
  parameters,
}: {
  path: string;
  parameters: string;
}) =>
  `https://dd-rum-proxy.updater.com/?ddforward=${encodeURIComponent(
    `https://browser-intake-datadoghq.com${path}?${parameters}`
  )}`;

// The deploy pipeline names the production environment `production` — it is
// the GitHub Environment the deploy records against and the .env file that
// gets selected. RUM is tagged `prod` across Updater services, so the name is
// translated here rather than renamed upstream.
const toDatadogEnv = (name: string) => (name === 'production' ? 'prod' : name);

/**
 * Sends an error to RUM without letting it reach the user as a crash.
 */
export const reportError = (
  error: Error,
  context?: Record<string, unknown>
) => {
  datadogRum.addError(error, context);
};

export const init = (env: EnvironmentVariables) => {
  datadogRum.init({
    applicationId: datadogConfig.APPLICATION_ID,
    clientToken: datadogConfig.CLIENT_TOKEN,
    defaultPrivacyLevel: 'mask-user-input',
    site: 'datadoghq.com',
    service: 'provider-app',
    proxy: buildProxyUrl,
    version: global.__VERSION_STRING__, // eslint-disable-line
    env: toDatadogEnv(env.name),
    sessionSampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    usePartitionedCrossSiteSessionCookie: true,
    enableExperimentalFeatures: ['feature_flags'],
    allowedTracingUrls: [/https.*api.*updater.com.*/],
    beforeSend: (event, context) => {
      if (event.type === 'resource') {
        let parsedBody;
        try {
          parsedBody =
            JSON.parse(
              (context as RumFetchResourceEventDomainContext).requestInit
                ?.body as string
            ) ?? {};
        } catch (e) {
          parsedBody = {};
        }
        if (
          event.resource.url.endsWith('/graphql') &&
          event.resource.method === 'POST'
        ) {
          // eslint-disable-next-line no-param-reassign
          event.context = {
            ...event.context,
            graphQLOperationName: parsedBody.operationName,
            graphQLOperationType: parsedBody.query
              ?.trim()
              .match(/^([\w]+)\s/)?.[1],
          };
        }
      }

      if (
        event.type === 'error' &&
        'error' in context &&
        errorIsApolloNetworkErrorWithCause(context.error)
      ) {
        event.context = {
          ...event.context,
          ...(context.error.networkError?.cause || {}),
        };
      }

      if (isInitialized()) {
        const fsSessionUrl = FullStory('getSession', { format: 'url' });
        event.context!.fullStoryUrl = fsSessionUrl; // eslint-disable-line
      }

      // setUser only tags events created after identity resolves, which leaves
      // errors thrown during the first render untagged. beforeSend runs at
      // flush time, so stamping promoId here attributes every event the
      // session sends once the promoId is known.
      const { promoId } = getCurrentProfile() ?? {};
      if (promoId) {
        event.context = { ...event.context, promoId };
      }

      return true;
    },
  });
  datadogRum.startSessionReplayRecording();
};
